import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import { useOverlayPopover } from '../useOverlayPopover'

// MPD-60 (MPD-58 #5) — text in a fixed one-line slot (App.css .trend-status-slot) is cut with
// an ellipsis when it does not fit, e.g. the slow-source and error wording at 390 px. `title`
// shows the full text only on hover, so whenever the text is cut an info button appears next
// to it and opens the full text in a popover that overlays what is below (absolutely
// positioned, App.css .status-text-full): the slot never grows and nothing moves. Escape and
// focus return are the shared popover behaviour; a tap or click outside closes it. Screen
// readers read the full text from the line itself, cut or not.
//
// MPD-62 (MPD-61 #1) — the overlay never outlives what the user opened it on:
// - `active` says whether the line holding this text is shown. A line that is kept mounted and
//   only hidden (visibility: hidden, to keep its slot) passes false while hidden: the overlay
//   closes, holds no Escape or outside-click listener, and the next time the line shows it
//   starts closed. A line that is unmounted closes it by unmounting. Default: shown.
// - A new text closes it, so it never shows wording the user did not open.
// - The overlay copy is aria-hidden: the line already holds the full text inside its live
//   region (role="status" / role="alert"), so opening it must not announce the message again.
export function StatusText({ text, active = true }: { text: string; active?: boolean }) {
  const id = useId()
  const textRef = useRef<HTMLSpanElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const wrapRef = useRef<HTMLSpanElement>(null)
  const [cut, setCut] = useState(false)
  const [open, setOpen] = useState(false)
  // `shown` gates everything, so even the render before the reset below holds no listener.
  const shown = open && active
  // MPD-64: Escape, outside press and one-overlay-at-a-time are shared with InfoTip.
  useOverlayPopover({ open: shown, close: () => setOpen(false), triggerRef, containerRef: wrapRef })
  useEffect(() => { if (!active) setOpen(false) }, [active])
  useEffect(() => { setOpen(false) }, [text])

  // Re-measure when the text or the available width changes. Once the button shows, the
  // text only gets narrower, so it stays cut: showing the button cannot flip it back.
  useLayoutEffect(() => {
    const el = textRef.current
    if (!el) return
    const measure = () => setCut(el.scrollWidth > el.clientWidth + 1)
    measure()
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null
    ro?.observe(el)
    return () => ro?.disconnect()
  }, [text])
  useEffect(() => { if (!cut) setOpen(false) }, [cut])

  return (
    <span ref={wrapRef} className="status-text">
      <span ref={textRef} className="trend-status-text" title={text}>{text}</span>
      {cut && (
        <button ref={triggerRef} type="button" className="status-text-more" aria-label="Show full message"
          aria-expanded={shown} aria-controls={shown ? id : undefined} onClick={() => setOpen((o) => !o)}>
          <span className="info-tip-glyph" aria-hidden="true">i</span>
        </button>
      )}
      {cut && shown && <span id={id} className="status-text-full" aria-hidden="true">{text}</span>}
    </span>
  )
}
